import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {$isAtNodeEnd} from '@lexical/selection';
import {$getNearestBlockElementAncestorOrThrow, mergeRegister} from '@lexical/utils';
import {isMentionMenuOpen} from 'components/DSYS/editor/utils/mentionMenuState';
import {
  $getSelection,
  $isElementNode,
  $isLineBreakNode,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_CRITICAL,
  KEY_ENTER_COMMAND,
  KEY_TAB_COMMAND,
  RangeSelection,
} from 'lexical';
import {useEffect} from 'react';

function $isCaretAtEndOfNonEmptyBlock(selection: RangeSelection): boolean {
  if (!selection.isCollapsed()) return false;

  const block = $getNearestBlockElementAncestorOrThrow(selection.anchor.getNode());
  if (block.getTextContent().trim() === '') return false;

  const anchor = selection.anchor;
  if (anchor.type === 'element' && $isElementNode(block) && anchor.getNode().is(block)) {
    return anchor.offset === block.getChildrenSize();
  }

  const lastDesc = block.getLastDescendant();
  if (!lastDesc) return false;

  if (!anchor.getNode().is(lastDesc)) return false;

  if ($isTextNode(lastDesc)) {
    return anchor.offset === lastDesc.getTextContentSize();
  }

  if ($isLineBreakNode(lastDesc)) {
    return $isAtNodeEnd(anchor);
  }

  return $isAtNodeEnd(anchor);
}

function focusNextTabStop(): void {
  if (typeof document === 'undefined') return;
  const active = document.activeElement;
  if (!active || !(active instanceof HTMLElement)) return;

  const selector =
    'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [contenteditable="true"], [tabindex]:not([tabindex="-1"])';
  const focusables = Array.from(document.querySelectorAll<HTMLElement>(selector)).filter((el) => {
    if (el.tabIndex === -1 && !el.isContentEditable) return false;
    const style = window.getComputedStyle(el);
    return style.visibility !== 'hidden' && style.display !== 'none';
  });

  const idx = focusables.indexOf(active);
  if (idx >= 0 && idx < focusables.length - 1) {
    focusables[idx + 1]?.focus();
  }
}

type FormKeyboardNavigationPluginProps = {
  focusNextOnTabAtBlockEnd?: boolean;
  onEnterSubmit?: () => void;
};

export const FormKeyboardNavigationPlugin = ({
  focusNextOnTabAtBlockEnd,
  onEnterSubmit,
}: FormKeyboardNavigationPluginProps) => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!focusNextOnTabAtBlockEnd && !onEnterSubmit) return;

    return mergeRegister(
      ...(focusNextOnTabAtBlockEnd
        ? [
            editor.registerCommand(
              KEY_TAB_COMMAND,
              (event) => {
                if (isMentionMenuOpen(editor)) return false;
                const selection = $getSelection();
                if (!$isRangeSelection(selection) || !selection.isCollapsed()) return false;
                if (!$isCaretAtEndOfNonEmptyBlock(selection)) return false;
                event?.preventDefault();
                focusNextTabStop();
                return true;
              },
              COMMAND_PRIORITY_CRITICAL
            ),
          ]
        : []),
      ...(onEnterSubmit
        ? [
            editor.registerCommand(
              KEY_ENTER_COMMAND,
              (event) => {
                if (event?.shiftKey) return false;
                // Enter picks the highlighted person in an open @mention menu.
                if (isMentionMenuOpen(editor)) return false;
                // Option+Enter (Mac) / Alt+Enter — newline, not submit
                if (event?.altKey) return false;
                event?.preventDefault();
                onEnterSubmit();
                return true;
              },
              COMMAND_PRIORITY_CRITICAL
            ),
          ]
        : [])
    );
  }, [editor, focusNextOnTabAtBlockEnd, onEnterSubmit]);

  return null;
};
